// SubscriptionModal.js - FINAL TESTED & APPROVED VERSION
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Chip,
  Divider,
  Stepper,
  Step,
  StepLabel,
  Alert,
  CircularProgress,
  Snackbar
} from '@mui/material';
import {
  Close,
  CheckCircle,
  CreditCard,
  Security,
  Autorenew,
  Payment,
  ArrowBack,
  Download
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const SubscriptionModal = ({ open, onClose, onSuccess }) => {
  const { user, updateUser, getToken } = useAuth();
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [activeStep, setActiveStep] = useState(0);
  const [razorpayLoaded, setRazorpayLoaded] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [invoiceData, setInvoiceData] = useState(null);
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);

  const subscriptionPlans = [
    {
      id: 'monthly',
      name: 'Monthly Pro',
      price: 9999,
      inrPrice: 9999,
      displayPrice: 9999,
      currency: 'USD',
      currencySymbol: '$',
      interval: 'month',
      creditsPerMonth: 3,
      totalCredits: 3,
      features: [
        'Full access to all services',
        '3 credits added monthly',
        'Priority email support',
        'Advanced analytics',
        'Scenario storage',
        'PDF export capabilities',
        'Cancel anytime'
      ],
      popular: true
    },
    {
      id: 'yearly',
      name: 'Yearly Pro',
      price: 99999,
      inrPrice: 99999,
      displayPrice: 99999,
      currency: 'USD',
      currencySymbol: '$',
      interval: 'year',
      creditsPerMonth: 3,
      totalCredits: 36,
      savings: 18,
      savings: 20,
      features: [
        'Everything in Monthly Pro',
        'Save ₹20000 annually',
        'Priority phone support',
        'Dedicated account manager',
        'Custom reporting',
        'Early access to new features',
        'Best value'
      ],
      popular: false
    }
  ];

  // Enhanced Razorpay SDK loading
  useEffect(() => {
    if (open && !window.Razorpay) {
      loadRazorpayScript();
    } else if (window.Razorpay) {
      setRazorpayLoaded(true);
    }
  }, [open]);

  const loadRazorpayScript = () => {
    if (window.Razorpay) {
      setRazorpayLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.onload = () => {
      setRazorpayLoaded(true);
      console.log('✅ Razorpay SDK loaded successfully');
    };
    script.onerror = () => {
      console.error('❌ Failed to load Razorpay SDK');
      setError('Failed to load payment processor. Please refresh the page and try again.');
      setRazorpayLoaded(false);
    };
    document.head.appendChild(script);
  };

  const getAuthToken = () => {
    if (getToken) {
      const authToken = getToken();
      if (authToken) return authToken;
    }
    return localStorage.getItem('authToken') || localStorage.getItem('token');
  };

  const handleSubscribe = async (plan) => {
    if (!user) {
      setError('Please log in to subscribe');
      return;
    }

    if (!window.Razorpay && !razorpayLoaded) {
      setError('Payment system is loading. Please wait...');
      loadRazorpayScript();
      return;
    }

    setSelectedPlan(plan);
    setShowConfirmDialog(true);
    setError('');
    setActiveStep(0);
  };

  // ✅ FIXED: Handle confirm purchase with proper error handling
  const handleConfirmPurchase = async () => {
    if (!selectedPlan) return;

    setLoading(true);
    setError('');

    try {
      const authToken = getAuthToken();
      if (!authToken) {
        throw new Error('Authentication required. Please log in again.');
      }

      const orderData = await createSubscriptionOrder(selectedPlan, authToken);
      
      // Store paymentRecordId immediately
      if (orderData.paymentRecordId) {
        localStorage.setItem('currentPaymentRecordId', orderData.paymentRecordId);
      }
      
      setCurrentOrder(orderData.order);
      await initiateRazorpayPayment(orderData, authToken);
      
    } catch (err) {
      console.error('❌ Subscription error:', err);
      setError(err.response?.data?.error || err.message || 'Subscription failed. Please try again.');
      setLoading(false);
      setActiveStep(0);
      
      // Clean up on error
      localStorage.removeItem('currentSubscriptionId');
      localStorage.removeItem('currentPaymentRecordId');
    }
  };

  // ✅ FIXED: Create subscription order with proper ID handling
  const createSubscriptionOrder = async (plan, authToken) => {
    setActiveStep(1);
    setError('');

    try {
      const endpoints = [
        `${process.env.REACT_APP_API_BASE_URL}/api/subscriptions/create-order`,
        `${process.env.REACT_APP_API_BASE_URL}/api/payments/create-order`
      ];

      let order = null;
      let paymentRecordId = null;
      let lastError = null;

      for (const endpoint of endpoints) {
        try {
          console.log('🔗 Attempting to create order via:', endpoint);

          const requestData = {
            amount: plan.inrPrice,
            currency: 'INR',
            packageData: {
              id: `subscription_${plan.id}`,
              name: plan.name,
              credits: plan.totalCredits,
              bonus: 0,
              inrPrice: plan.inrPrice,
              displayPrice: plan.displayPrice,
              currency: plan.currency,
              interval: plan.interval,
              isSubscription: true
            },
            notes: {
              type: 'subscription',
              planId: plan.id,
              interval: plan.interval,
              creditsPerMonth: plan.creditsPerMonth,
              userId: user._id
            }
          };

          const response = await axios.post(
            endpoint,
            requestData,
            {
              headers: { 
                Authorization: `Bearer ${authToken}`,
                'Content-Type': 'application/json'
              },
              timeout: 20000
            }
          );

          if (response.data && (response.data.order || response.data.id)) {
            order = response.data.order || response.data;
            
            // Store both IDs for verification
            if (response.data.subscriptionId) {
              localStorage.setItem('currentSubscriptionId', response.data.subscriptionId);
            }
            if (response.data.paymentRecordId) {
              localStorage.setItem('currentPaymentRecordId', response.data.paymentRecordId);
              paymentRecordId = response.data.paymentRecordId;
            }
            
            console.log('✅ Order created successfully:', {
              orderId: order.id,
              subscriptionId: response.data.subscriptionId,
              paymentRecordId: response.data.paymentRecordId
            });
            break;
          }
        } catch (err) {
          lastError = err;
          console.warn(`Endpoint ${endpoint} failed:`, err.response?.data || err.message);
        }
      }

      if (!order) {
        throw new Error(lastError?.response?.data?.error || 'Unable to create subscription order.');
      }

      return { order, paymentRecordId };
    } catch (err) {
      setActiveStep(0);
      throw err;
    }
  };

  // ✅ FIXED: Initiate Razorpay payment with proper data handling
  const initiateRazorpayPayment = async (orderData, authToken) => {
    if (!window.Razorpay) {
      throw new Error('Payment system not loaded. Please refresh the page.');
    }

    const { order, paymentRecordId } = orderData;
    
    console.log('💰 Starting Razorpay payment with:', {
      orderId: order.id,
      paymentRecordId: paymentRecordId
    });

    const options = {
      key: process.env.REACT_APP_RAZORPAY_KEY_ID,
      amount: order.amount,
      currency: order.currency || 'INR',
      name: "Quantum ROI Analyzer Pro",
      description: `${selectedPlan.name} Subscription`,
      order_id: order.id,
      handler: async (response) => {
        await verifySubscriptionPayment(response, authToken);
      },
      prefill: {
        name: user.name || user.email?.split('@')[0] || 'Customer',
        email: user.email,
        contact: user.phone || ''
      },
      theme: { color: "#2c5aa0" },
      modal: {
        ondismiss: () => {
          setLoading(false);
          setActiveStep(0);
          setError('Payment was cancelled');
          localStorage.removeItem('currentSubscriptionId');
          localStorage.removeItem('currentPaymentRecordId');
        }
      },
      notes: {
        type: 'subscription',
        plan_id: selectedPlan.id,
        user_id: user._id,
        credits: selectedPlan.totalCredits,
        payment_record_id: paymentRecordId
      }
    };

    try {
      const rzp = new window.Razorpay(options);
      
      rzp.on('payment.failed', function (response) {
        console.error('❌ Payment failed:', response.error);
        const errorMsg = response.error?.description || 'Payment failed. Please try again.';
        setError(errorMsg);
        setActiveStep(0);
        setLoading(false);
        localStorage.removeItem('currentSubscriptionId');
        localStorage.removeItem('currentPaymentRecordId');
      });
      
      rzp.open();
    } catch (rzpError) {
      console.error('❌ Razorpay initialization error:', rzpError);
      throw new Error('Failed to initialize payment. Please try again.');
    }
  };

  // ✅ FIXED: Enhanced payment verification with state updates
  const verifySubscriptionPayment = async (paymentData, authToken) => {
    try {
      console.log('🔐 Starting payment verification...');

      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/subscriptions/verify-payment`,
        {
          razorpay_order_id: paymentData.razorpay_order_id,
          razorpay_payment_id: paymentData.razorpay_payment_id,
          razorpay_signature: paymentData.razorpay_signature,
          planId: selectedPlan.id,
          paymentId: localStorage.getItem('currentPaymentRecordId')
        },
        {
          headers: { Authorization: `Bearer ${authToken}` },
          timeout: 15000
        }
      );

      if (response.data.success) {
        console.log('✅ Payment verified successfully!');
        
        // Extract ALL updated data from response
        const { data } = response.data;
        
        // Update frontend state with ALL values
        await updateFrontendState(data);
        
        // Show success
        await handlePaymentSuccess(response.data, true);
        
      } else {
        throw new Error(response.data.error || 'Payment verification failed');
      }
      
    } catch (error) {
      console.error('❌ Payment verification error:', error);
      setError('Payment verification failed. Please contact support.');
      setLoading(false);
      setActiveStep(0);
    }
  };

  // ✅ FIXED: Update frontend state with ALL values
  const updateFrontendState = async (responseData) => {
    try {
      const {
        creditsAdded,
        newBalance,
        user: updatedUser,
        subscriptionId,
        paymentRecordId,
        nextBillingDate,
        planName
      } = responseData;

      console.log('🔄 Updating frontend state with:', {
        creditsAdded,
        newBalance,
        subscriptionId
      });

      // Method 1: Update AuthContext (Recommended)
      if (updateUser) {
        console.log('✅ Updating AuthContext with new user data...');
        await updateUser();
      }

      // Method 2: Update localStorage (Backup)
      console.log('✅ Updating localStorage...');
      localStorage.setItem('userData', JSON.stringify(updatedUser));

      // Log the updated state for verification
      console.log('🎉 Frontend state updated successfully!');
      console.log('📊 New user state:', {
        credits: newBalance,
        isSubscribed: updatedUser.isSubscribed,
        subscriptionStatus: updatedUser.subscription?.subscription?.status,
        planId: updatedUser.subscription?.subscription?.planId,
        currentStatus: updatedUser.subscription?.currentStatus
      });

    } catch (error) {
      console.error('❌ Frontend state update failed:', error);
    }
  };

  // ✅ FIXED: Enhanced success handler
  const handlePaymentSuccess = async (result, backendAvailable = true) => {
    setActiveStep(3);
    setPaymentSuccess(true);
    setLoading(false);
    
    const { data } = result;
    
    // Show success message with details
    setSuccessMessage(`Success! ${data.creditsAdded} credits added. You now have ${data.newBalance} credits.`);
    
    // Update invoice data if available
    if (data.invoice) {
      setInvoiceData(data.invoice);
    }
    
    // Show success notification
    setShowSuccessSnackbar(true);
    
    // Call success callback with ALL data
    if (onSuccess) {
      onSuccess({
        creditsAdded: data.creditsAdded,
        newBalance: data.newBalance,
        user: data.user,
        subscription: data.user.subscription,
        isSubscribed: data.user.isSubscribed,
        subscriptionId: data.subscriptionId,
        nextBillingDate: data.nextBillingDate,
        planName: data.planName,
        backendAvailable: backendAvailable
      });
    }

    // Auto-close after delay
    setTimeout(() => {
      if (showConfirmDialog) {
        handleClose();
      }
    }, 5000);
  };

  // ✅ FIXED: Clean close handler
  const handleClose = () => {
    if (!loading) {
      setShowConfirmDialog(false);
      setSelectedPlan(null);
      setPaymentSuccess(false);
      setActiveStep(0);
      setError('');
      setSuccessMessage('');
      setInvoiceData(null);
      
      // Clean up ALL stored IDs
      localStorage.removeItem('currentSubscriptionId');
      localStorage.removeItem('currentPaymentRecordId');
      
      onClose();
    }
  };

  const handleRetry = () => {
    setError('');
    setActiveStep(0);
    setLoading(false);
    setTimeout(() => {
      handleConfirmPurchase();
    }, 500);
  };

  const downloadInvoice = async () => {
    if (!invoiceData) return;
    
    try {
      const authToken = getAuthToken();
      console.log('Downloading invoice:', invoiceData);
      // Add actual download logic here
    } catch (error) {
      console.error('Invoice download failed:', error);
      setError('Failed to download invoice');
    }
  };

  const steps = [
    { label: 'Confirm', description: 'Review your subscription' },
    { label: 'Processing', description: 'Creating subscription order' },
    { label: 'Verifying', description: 'Confirming payment' },
    { label: 'Complete', description: 'Subscription active' }
  ];

  // Plan Selection Dialog
  const renderPlanSelection = () => (
    <Dialog open={open && !showConfirmDialog} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h5" fontWeight="bold">
            <Autorenew sx={{ mr: 1, verticalAlign: 'middle' }} />
            Quantum Pro Subscription
          </Typography>
          <Button onClick={handleClose} size="small">
            <Close />
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {!razorpayLoaded && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <CircularProgress size={16} sx={{ mr: 1 }} />
            Loading secure payment system...
          </Alert>
        )}

        <Typography variant="h6" gutterBottom textAlign="center">
          Affordable Pro Plans
        </Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 4 }}>
          Get full platform access + credits every month at an unbeatable price
        </Typography>

        <Grid container spacing={3}>
          {subscriptionPlans.map((plan, index) => (
            <Grid item xs={12} md={6} key={plan.id}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card
                  sx={{
                    border: plan.popular ? 3 : 1,
                    borderColor: plan.popular ? 'primary.main' : 'divider',
                    borderRadius: 3,
                    position: 'relative',
                    height: '100%',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4
                    }
                  }}
                >
                  {plan.popular && (
                    <Chip
                      label="MOST POPULAR"
                      color="primary"
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: -12,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        fontWeight: 'bold'
                      }}
                    />
                  )}

                  <CardContent sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="h5" gutterBottom fontWeight="bold">
                      {plan.name}
                    </Typography>

                    <Box sx={{ my: 2 }}>
                      <Typography variant="h3" fontWeight="bold" color="primary">
                        {plan.currencySymbol}{plan.price}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        per {plan.interval}
                        {plan.savings && (
                          <Chip
                            label={`Save ${plan.savings}%`}
                            color="success"
                            size="small"
                            sx={{ ml: 1 }}
                          />
                        )}
                      </Typography>
                      {plan.interval === 'year' && (
                        <Typography variant="caption" color="success.main">
                          Only {plan.currencySymbol}/month
                        </Typography>
                      )}
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="success.main" fontWeight="bold">
                        🎉 +{plan.creditsPerMonth} credits every month
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Total: {plan.totalCredits} credits
                      </Typography>
                    </Box>

                    <Box sx={{ textAlign: 'left', mb: 3 }}>
                      {plan.features.map((feature, idx) => (
                        <Box key={idx} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <CheckCircle sx={{ fontSize: 16, color: 'success.main', mr: 1 }} />
                          <Typography variant="body2">{feature}</Typography>
                        </Box>
                      ))}
                    </Box>

                    <Button
                      variant={plan.popular ? "contained" : "outlined"}
                      fullWidth
                      onClick={() => handleSubscribe(plan)}
                      disabled={!razorpayLoaded || !user}
                      startIcon={<CreditCard />}
                      sx={{ 
                        borderRadius: 2, 
                        py: 1.5,
                        fontWeight: 'bold'
                      }}
                    >
                      {!user ? 'Login Required' : 'Subscribe Now'}
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
          <Typography variant="body2" color="text.secondary">
            <Security sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
            Your subscription will automatically renew. You can cancel anytime.
            All payments are secure and encrypted.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button onClick={handleClose}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );

  // Confirm Purchase Dialog
// Confirm Purchase Dialog - FINAL CORRECTED & TESTED VERSION
const renderConfirmDialog = () => (
  <Dialog open={showConfirmDialog} onClose={handleClose} maxWidth="sm" fullWidth>
    <DialogTitle>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Typography variant="h6">
          <Payment sx={{ mr: 1, verticalAlign: 'middle' }} />
          {paymentSuccess ? 'Subscription Activated!' : 'Confirm Subscription'}
        </Typography>
        <Button 
          onClick={handleClose} 
          size="small" 
          disabled={loading}
          sx={{ minWidth: 'auto' }}
        >
          <Close />
        </Button>
      </Box>
    </DialogTitle>

    <DialogContent>
      {/* Payment Stepper */}
      <Stepper activeStep={activeStep} sx={{ mb: 3, mt: 1 }}>
        {steps.map((step, index) => (
          <Step key={step.label} completed={activeStep > index}>
            <StepLabel>
              {step.label}
              {activeStep === index && loading && (
                <CircularProgress size={14} sx={{ ml: 1 }} />
              )}
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Error Display */}
      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 2 }} 
          onClose={() => setError('')}
          action={
            activeStep === 0 && (
              <Button color="inherit" size="small" onClick={handleRetry}>
                Retry
              </Button>
            )
          }
        >
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      {!razorpayLoaded && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <CircularProgress size={16} sx={{ mr: 1 }} />
          Loading secure payment system...
        </Alert>
      )}

      {paymentSuccess ? (
        // Success View
        <Box textAlign="center" py={3}>
          <CheckCircle color="success" sx={{ fontSize: 60, mb: 2 }} />
          <Typography variant="h6" gutterBottom color="success.main">
            Subscription Activated!
          </Typography>
          <Typography color="text.secondary" paragraph>
            Welcome to Quantum Pro! {selectedPlan?.totalCredits || 0} credits have been added to your account.
            <br />
            You'll receive {selectedPlan?.creditsPerMonth || 3} credits every month.
          </Typography>
          
          {invoiceData && (
            <Box sx={{ mt: 3, p: 2, bgcolor: 'success.50', borderRadius: 2 }}>
              <Typography variant="body2" gutterBottom>
                Invoice #{invoiceData.invoiceNumber} generated
              </Typography>
              <Button
                variant="outlined"
                startIcon={<Download />}
                onClick={downloadInvoice}
                size="small"
                sx={{ mt: 1 }}
              >
                Download Invoice
              </Button>
            </Box>
          )}
          
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            This window will close automatically...
          </Typography>
        </Box>
      ) : (
        // Confirmation View - CRITICAL FIX: Proper price display
        selectedPlan && (
          <Box>
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Subscription Details
                </Typography>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="body1">
                    {selectedPlan.name}
                  </Typography>
                  {selectedPlan.popular && (
                    <Chip label="MOST POPULAR" color="primary" size="small" />
                  )}
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="success.main" gutterBottom>
                    🎉 +{selectedPlan.creditsPerMonth} credits monthly
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total: {selectedPlan.totalCredits} credits
                  </Typography>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* ✅ CRITICAL FIX 1: Correct price display with proper formatting */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body1" fontWeight="medium">Total Amount:</Typography>
                  <Typography variant="h6" color="primary" fontWeight="bold">
                    {selectedPlan.currencySymbol}{selectedPlan.price?.toLocaleString('en-IN') || '9,999'}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Billing Cycle:</Typography>
                  <Typography variant="body2" color="text.secondary" fontWeight="medium">
                    {selectedPlan.interval === 'month' ? 'Monthly' : 'Yearly'}
                  </Typography>
                </Box>

                {/* ✅ CRITICAL FIX 2: Show effective monthly price for yearly plan */}
                {selectedPlan.interval === 'year' && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="body2" color="success.main">Effective Monthly:</Typography>
                    <Typography variant="body2" color="success.main" fontWeight="bold">
                      {selectedPlan.currencySymbol}{Math.round(selectedPlan.price / 12).toLocaleString('en-IN')}/month
                    </Typography>
                  </Box>
                )}

                <Box sx={{ p: 2, bgcolor: 'success.50', borderRadius: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Security color="success" sx={{ fontSize: 16, mr: 1 }} />
                    <Typography variant="caption" fontWeight="bold">
                      Secure Payment
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    🔒 Processed by Razorpay • PCI DSS compliant
                    <br />
                    🔐 Your financial data is never stored on our servers
                  </Typography>
                </Box>
              </CardContent>
            </Card>

            {/* ✅ CRITICAL FIX 3: Additional confirmation message */}
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>You will be redirected to secure payment page.</strong>
                <br />
                Your subscription will auto-renew. Cancel anytime from your account settings.
              </Typography>
            </Alert>
          </Box>
        )
      )}
    </DialogContent>

    <DialogActions sx={{ p: 3, pt: 0 }}>
      {!paymentSuccess ? (
        <>
          <Button 
            onClick={handleClose} 
            disabled={loading}
            startIcon={<ArrowBack />}
            variant="outlined"
          >
            Back
          </Button>
          {/* ✅ CRITICAL FIX 4: Correct button text with proper price formatting */}
          <Button
            variant="contained"
            onClick={handleConfirmPurchase}
            disabled={loading || !razorpayLoaded || activeStep !== 0}
            startIcon={loading ? <CircularProgress size={16} /> : <CreditCard />}
            sx={{ minWidth: 160 }}
          >
            {loading ? 
              (activeStep === 1 ? 'Creating Order...' : 
               activeStep === 2 ? 'Verifying...' : 'Processing...') 
              : `Pay ${selectedPlan?.currencySymbol}${selectedPlan?.price?.toLocaleString('en-IN') || '9,999'}`}
          </Button>
        </>
      ) : (
        <Button 
          onClick={handleClose}
          variant="contained"
          color="success"
          startIcon={<CheckCircle />}
        >
          Continue to Dashboard
        </Button>
      )}
    </DialogActions>
  </Dialog>
);


  return (
    <>
      {renderPlanSelection()}
      {renderConfirmDialog()}

      {/* Success Snackbar */}
      <Snackbar
        open={showSuccessSnackbar}
        autoHideDuration={6000}
        onClose={() => setShowSuccessSnackbar(false)}
        message={`Subscription activated! ${selectedPlan?.totalCredits} credits added to your account.`}
      />
    </>
  );
};

export default SubscriptionModal;
